import { useSocialEngagementAnalysis } from "@/components/QuestModals/SocialEngagementModal/useSocialEngagementAnalysis";
import { MODAL_HEIGHT, MODAL_WIDTH, appConfig } from "@/constants";
import { ConfirmationDialog, Stack } from "@biom3/react";
import { useCallback, useEffect, useMemo } from "react";
import { getModalConfig } from "./modalConfig";

export type SocialEngagementModalProps = {
  visible: boolean;
  onClose: () => void;
  gameId: string;
  gameName: string;
  rewardType: "points" | "gems";
  hasMultiplier?: boolean;
  questId?: string;
};

export const SocialEngagementModal = ({
  visible,
  onClose,
  gameId,
  gameName,
  rewardType,
  hasMultiplier,
  questId,
}: SocialEngagementModalProps) => {
  const {
    data,
    modalState,
    startAnalysis,
    retryAnalysis,
    userLeaderboardRank,
  } = useSocialEngagementAnalysis(gameId);

  useEffect(() => {
    if (visible) {
      startAnalysis();
    }
  }, [visible, startAnalysis]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const modalConfig = useMemo(() => {
    return getModalConfig({
      modalState,
      data,
      gameId,
      gameName,
      userLeaderboardRank,
      onClose: handleClose,
      onConnect: handleClose,
      retryAnalysis,
      rewardType,
      hasMultiplier,
      questId,
    });
  }, [
    modalState,
    data,
    gameId,
    gameName,
    userLeaderboardRank,
    handleClose,
    retryAnalysis,
    rewardType,
    hasMultiplier,
    questId,
  ]);

  return (
    <ConfirmationDialog visible={visible}>
      <ConfirmationDialog.Content
        sx={{
          d: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "space-between",
          borderRadius: "base.borderRadius.x10",
          position: "relative",
          overflow: "hidden",
          background: "base.color.brand.2",
          backgroundImage: `url("${appConfig.ASSET_BASE_URL}/images/quests/follow-confirmation-background.svg")`,
          backgroundRepeat: "no-repeat",
          backgroundPosition: "top",
          height: MODAL_HEIGHT,
          width: {
            default: "calc(100dvw - 40px)",
            small: MODAL_WIDTH,
          },
        }}
      >
        <ConfirmationDialog.Content.Title sx={{ mt: "base.spacing.x10" }}>
          {modalConfig.title}
        </ConfirmationDialog.Content.Title>

        <ConfirmationDialog.Content.Caption>
          <Stack
            direction="column"
            gap="base.spacing.x10"
            justifyContent="space-between"
          >
            {modalConfig.caption}
            {modalConfig.body}
          </Stack>
        </ConfirmationDialog.Content.Caption>

        {modalConfig.actions}
        <ConfirmationDialog.Content.CloseButtCon onClick={handleClose} />
      </ConfirmationDialog.Content>
    </ConfirmationDialog>
  );
};
